import type { components, operations } from '@/api'
import type { Field, ReviewStatus } from '@/modules/Project/Fields/types'
import { toBackendFilter } from '@/modules/Project/Filters/types'
import { invariant } from '@/shared/utils/typeAssertions'
import type { AxiosRequestConfig } from 'axios'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { get, post, put, remove } from './client'
import type { BackendMeta } from './types'
import { PropertyType } from './types'

export type AddEntityPath = `/api/workspaces/${string}/projects/${string}/entities`

export type AddEntityRequest = Exclude<
  operations['entity-create']['requestBody'],
  undefined
>['content']['application/json']
export type AddEntityResponse =
  operations['entity-create']['responses']['200']['content']['application/json']

type AddEntityParams = {
  workspaceId: string
  projectId: string
  parentEntityId?: AddEntityRequest['parent_entity_id']
  fields?: AddEntityRequest['fields']
}

export const addEntity = ({ parentEntityId, projectId, workspaceId, fields }: AddEntityParams) => {
  return post<AddEntityRequest, AddEntityResponse, AddEntityPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities`,
    {
      parent_entity_id: parentEntityId,
      fields,
    },
  )
}

export type DeleteEntity = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/entities/{entity_id}',
  'delete'
>

type DeleteEntityParams = {
  workspaceId: string
  projectId: string
  entityId: string
}

export const deleteEntity = ({ entityId, projectId, workspaceId }: DeleteEntityParams) =>
  remove<undefined, DeleteEntity['successResponse'], DeleteEntity['path']>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}`,
    undefined,
  )

export type BulkRemoveEntityPath =
  `/api/workspaces/${string}/projects/${string}/entities/bulk_delete`

export type BulkRemoveEntityRequest = Exclude<
  operations['entity-bulk-delete']['requestBody'],
  undefined
>['content']['application/json']

export type BulkRemoveEntityResponse =
  operations['entity-bulk-delete']['responses']['200']['content']['application/json']

export const bulkRemoveEntities = (workspaceId: string, projectId: string, entityIds: string[]) =>
  post<BulkRemoveEntityRequest, BulkRemoveEntityResponse, BulkRemoveEntityPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/bulk_delete`,
    {
      entity_ids: entityIds,
    },
  )

export type GetEntityPath = `/api/workspaces/${string}/projects/${string}/entities/${string}`
export type GetEntityRequest = undefined
export type GetEntityResponse =
  operations['entity-get']['responses']['200']['content']['application/json']

export const getEntity = (workspaceId: string, projectId: string, entityId: string) =>
  get<GetEntityRequest, GetEntityResponse, GetEntityPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}`,
    undefined,
  )

type ListEntitiesMeta = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/entities',
  'get'
>
export type ListEntitiesPath = ListEntitiesMeta['path']
export type ListEntitiesResponse = ListEntitiesMeta['successResponse']
export type ListEntitiesRequest = ListEntitiesMeta['queryParams']

export const listEntities = (params: {
  workspaceId: string
  projectId: string
  limit?: number | undefined
  offset?: number
  parent_entity_id?: string
  viewId?: string
  order_by?: string[]
  order_directions?: ListEntitiesRequest['order_directions']
  after?: ListEntitiesRequest['after']
  first?: ListEntitiesRequest['first']
  truncateDataBytes?: ListEntitiesRequest['truncate_data_bytes']
}) => {
  return get<ListEntitiesRequest, ListEntitiesResponse, ListEntitiesPath>(
    `/api/workspaces/${params.workspaceId}/projects/${params.projectId}/entities`,
    {
      order_by: params.order_by,
      limit: params.limit,
      offset: params.offset,
      parent_entity_id: params.parent_entity_id,
      active_view_ids: params.viewId !== undefined ? [params.viewId] : undefined,
      order_directions: params.order_directions,
      after: params.after,
      first: params.first,
      truncate_data_bytes: params.truncateDataBytes,
    },
  )
}

export type ListEntityIdsPath = `/api/workspaces/${string}/projects/${string}/entity_ids`
export type ListEntityIdsResponse =
  components['schemas']['Projects.ListEntities.ListEntityIdsResponse']
export type ListEntityIdsRequest = operations['entity-id-list']['parameters']['query']

export type ListEntityIdsArgs = {
  workspaceId: string
  projectId: string
  parent_entity_id?: string
  active_view_id?: string
} & (
  | {
      after: string
      first: number
      before?: undefined
      last?: undefined
    }
  | {
      before: string
      last: number
      after?: undefined
      first?: undefined
    }
)

export const listEntityIds = ({
  workspaceId,
  projectId,
  after,
  first,
  before,
  last,
  parent_entity_id,
  active_view_id,
}: ListEntityIdsArgs) =>
  get<ListEntityIdsRequest, ListEntityIdsResponse, ListEntityIdsPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entity_ids`,
    {
      order_by: ['id'],
      after,
      first,
      before,
      last,
      parent_entity_id,
      active_view_id: active_view_id,
    },
  )

export type ListFilteredEntitiesPath =
  `/api/workspaces/${string}/projects/${string}/entities/filter`
export type ListFilteredEntitiesResponse =
  components['schemas']['Projects.ListEntities.ListEntitiesResponse']
export type ListFilteredEntitiesRequest = components['schemas']['Projects.ListEntitiesRequest']

export type ListFilteredEntitiesArgs = {
  workspaceId: string
  projectId: string
  order_by?: string[]
  order_directions?: ListFilteredEntitiesRequest['order_directions']
} & ListFilteredEntitiesRequest

export const listFilteredEntities = ({
  workspaceId,
  projectId,
  ...requestBody
}: ListFilteredEntitiesArgs) =>
  post<ListFilteredEntitiesRequest, ListFilteredEntitiesResponse, ListFilteredEntitiesPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/filter`,
    requestBody,
  )

export type ListFilteredEntityIdsPath =
  `/api/workspaces/${string}/projects/${string}/entity_ids/filter`
export type ListFilteredEntityIdsResponse =
  components['schemas']['Projects.ListEntities.ListEntityIdsResponse']
export type ListFilteredEntityIdsRequest = components['schemas']['Projects.ListEntitiesRequest']

export type ListFilteredEntityIdsArgs = {
  workspaceId: string
  projectId: string
  requestConfig?: AxiosRequestConfig
} & ListFilteredEntityIdsRequest

export const listFilteredEntityIds = ({
  workspaceId,
  projectId,
  requestConfig,
  ...requestBody
}: ListFilteredEntityIdsArgs) =>
  post<ListFilteredEntityIdsRequest, ListFilteredEntityIdsResponse, ListFilteredEntityIdsPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entity_ids/filter`,
    requestBody,
    requestConfig,
  )

export type RecalculateEntitiesPath =
  `/api/workspaces/${string}/projects/${string}/entities/recalculate`
export type RecalculateEntitiesRequest = Exclude<
  operations['entity-recalculate-all']['requestBody'],
  undefined
>['content']['application/json']
export type RecalculateEntitiesResponse = Exclude<
  operations['entity-recalculate-all']['responses']['200'],
  undefined
>['content']['application/json']

export const recalculateEntitites = ({
  workspaceId,
  projectId,
  propertyIds,
}: {
  workspaceId: string
  projectId: string
  propertyIds: string[]
}) =>
  post<RecalculateEntitiesRequest, RecalculateEntitiesResponse, RecalculateEntitiesPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/recalculate`,
    {
      property_ids: propertyIds,
      force: false,
    },
  )

export type RecalculateEntityPath =
  `/api/workspaces/${string}/projects/${string}/entities/${string}/recalculate`
export type RecalculateEntityRequest = Exclude<
  operations['entity-recalculate-single']['requestBody'],
  undefined
>['content']['application/json']
export type RecalculateEntityResponse = Exclude<
  operations['entity-recalculate-single']['responses']['200'],
  undefined
>['content']['application/json']

/**
 * Recalculate the tool values for a list of fields on the given entity.
 *
 * @param params
 * @param params.propertyIds - property IDs of all fields to recalculate.
 * @param params.force - `false` will do nothing if the hashes match. `true` will force a recalculation even if the hashes match.
 */
export const recalculateEntity = ({
  entityId,
  projectId,
  propertyIds,
  workspaceId,
  force = false,
}: {
  workspaceId: string
  projectId: string
  entityId: string
  propertyIds: string[]
  /**
   * - `force: false` will do nothing if the hashes match.
   * - `force: true` will force a recalculation even if the hashes match.
   */
  force?: boolean
}) =>
  post<RecalculateEntityRequest, RecalculateEntityResponse, RecalculateEntityPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}/recalculate`,
    {
      property_ids: propertyIds,
      force,
    },
  )

export type SetEntityMetadata = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/entities/{entity_id}/properties/{property_id_or_slug}/set_metadata',
  'put'
>

type SetEntityMetadataParams = {
  workspaceId: SetEntityMetadata['pathParams']['workspace_id']
  projectId: SetEntityMetadata['pathParams']['project_id']
  entityId: SetEntityMetadata['pathParams']['entity_id']
  propertyIdOrSlug: SetEntityMetadata['pathParams']['property_id_or_slug']
  score?: SetEntityMetadata['body']['value']['value_score']
  fieldName?: SetEntityMetadata['body']['value']['field_name']
}

export const setEntityMetadata = ({
  workspaceId,
  projectId,
  entityId,
  propertyIdOrSlug,
  score,
  fieldName,
}: SetEntityMetadataParams) =>
  put<SetEntityMetadata['body'], SetEntityMetadata['successResponse'], SetEntityMetadata['path']>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}/properties/${propertyIdOrSlug}/set_metadata`,
    { value: { value_score: score, field_name: fieldName } },
  )

export type SetFieldValuePath = `/api/workspaces/${string}/projects/${string}/entities/${string}`
export type SetFieldValueRequest = components['schemas']['update_entity']

export type UpdateEntityFieldValue = Extract<
  components['schemas']['field_value'],
  Record<string, unknown> | null
>

export type PutEntityResponse = components['schemas']['Projects.Entities.EntityResponse']

export type SetFieldValueParams = {
  workspaceId: string
  projectId: string
  entityId: string
  // Mapping of property ID to new value
  fields: Record<string, UpdateEntityFieldValue>
}

/**
 * Updates the field values for an entity. Can update any number of fields
 * for the entity.
 */
export const setFieldValue = ({
  workspaceId,
  projectId,
  entityId,
  fields,
}: SetFieldValueParams) => {
  return put<SetFieldValueRequest, PutEntityResponse, SetFieldValuePath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}`,
    { fields },
  )
}

/** Gets a payload for this endpoint from a field type and value */
export const getSetFieldValuePayload = (
  fieldType: PropertyType,
  manualValue: Field['manualValue'],
): UpdateEntityFieldValue => {
  switch (fieldType) {
    case PropertyType.reference:
      invariant(
        typeof manualValue === 'object' && !Array.isArray(manualValue),
        'manualValue is not a reference',
      )
      return {
        reference: toBackendFilter(manualValue) ?? null,
      }
    case PropertyType.single_select:
    case PropertyType.multi_select:
    case PropertyType.user_select:
    case PropertyType.hub_select:
    case PropertyType.file_collection:
    case PropertyType.collection: {
      invariant(Array.isArray(manualValue), 'manualValue is not an array')
      return {
        options: manualValue,
      }
    }
    case PropertyType.data:
    case PropertyType.file: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { file_url: manualValue }
    }
    case PropertyType.json: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { json: manualValue }
    }
    case PropertyType.url: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { url: manualValue }
    }
    case PropertyType.text: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { text: manualValue }
    }
    case PropertyType.number: {
      invariant(typeof manualValue === 'string', 'manualValue is not a string')
      return { text: manualValue }
    }
    default: {
      const exhaustiveCheck: never = fieldType
      throw new Error(`Unhandled field type: ${exhaustiveCheck}`)
    }
  }
}

type SetGroundTruthMeta = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/entities/{entity_id}/properties/{property_id_or_slug}/ground_truth',
  'put'
>
export type SetGroundTruthPath = SetGroundTruthMeta['path']
export type SetGroundTruthResponse = SetGroundTruthMeta['successResponse']
export type SetGroundTruthRequest = SetGroundTruthMeta['body']
export type SetGroundTruthParams = SetGroundTruthMeta['pathParams'] & SetGroundTruthMeta['body']

export const setGroundTruth = ({
  workspace_id,
  project_id,
  entity_id,
  property_id_or_slug,
  ground_truth,
}: SetGroundTruthParams) => {
  return post<SetGroundTruthRequest, SetGroundTruthResponse, SetGroundTruthPath>(
    `/api/workspaces/${workspace_id}/projects/${project_id}/entities/${entity_id}/properties/${property_id_or_slug}/ground_truth`,
    { ground_truth },
  )
}

export type SkipFieldPath =
  `/api/workspaces/${string}/projects/${string}/entities/${string}/properties/${string}/skip`
export type SkipFieldRequest = Exclude<
  operations['entity-skip-field']['requestBody'],
  undefined
>['content']['application/json']

export type SkipFieldResponse =
  operations['entity-skip-field']['responses']['200']['content']['application/json']

/**
 * Skips a field by marking it as skipped. This can only be used on fields that are in the 'idle' state.
 */
export const skipField = (
  workspaceId: string,
  projectId: string,
  entityId: string,
  propertyId: string,
) =>
  post<SkipFieldRequest, SkipFieldResponse, SkipFieldPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}/properties/${propertyId}/skip`,
    {},
  )

export type StartFieldFileUploadPath =
  `/api/workspaces/${string}/projects/${string}/entities/${string}/properties/${string}/start_file_upload`
export type StartFieldFileUploadRequest = Exclude<
  operations['entity-start-file-upload']['requestBody'],
  undefined
>['content']['application/json']

export type StartFieldFileUploadResponse =
  operations['entity-start-file-upload']['responses']['200']['content']['application/json']

/**
 * Fires a network request to he backend to provide a file url to upload a file to
 * The backend action will generate a presigned url for the file and return it,
 * and also save it onto the manual_value of the given field.
 */
export const startFieldFileUpload = (
  workspaceId: string,
  projectId: string,
  entityId: string,
  propertyId: string,
  filename: string,
) =>
  post<StartFieldFileUploadRequest, StartFieldFileUploadResponse, StartFieldFileUploadPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/entities/${entityId}/properties/${propertyId}/start_file_upload`,
    { filename },
  )

export type ConfirmFieldFileUploadPath =
  `/api/workspaces/${string}/projects/${string}/entities/${string}/properties/${string}/confirm_file_upload`
export type ConfirmFieldFileUploadRequest = undefined
export type ConfirmFieldFileUploadResponse =
  operations['entity-confirm-file-upload']['responses']['200']['content']['application/json']

/**
 * Confirms that a file has been uploaded to the s3 url stored in the given field's input value
 */
export const confirmFieldFileUpload = async (url: string) => {
  const apiPath = url.replace(/^.+?(\/api\/)/, '$1')
  if (!isConfirmFieldFileEndpoint(apiPath)) {
    throw new Error('url is not a valid confirmFieldFileUpload endpoint.')
  }
  return post<
    ConfirmFieldFileUploadRequest,
    ConfirmFieldFileUploadResponse,
    ConfirmFieldFileUploadPath
  >(apiPath, undefined)
}

function isConfirmFieldFileEndpoint(path: string): path is ConfirmFieldFileUploadPath {
  const validPattern =
    /^\/api\/workspaces\/[^/]+\/projects\/[^/]+\/entities\/[^/]+\/properties\/[^/]+\/confirm_file_upload$/
  return validPattern.test(path)
}

export type SetReviewStatus = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/entities/{entity_id}/properties/{property_id_or_slug}/review_status',
  'put'
>
export type SetReviewStatusParams = {
  workspaceId: MaybeRefOrGetter<string>
  projectId: MaybeRefOrGetter<string>
  entityId: MaybeRefOrGetter<string>
  propertyId: MaybeRefOrGetter<string>
  reviewStatus: MaybeRefOrGetter<ReviewStatus>
}

export const setReviewStatus = ({
  entityId,
  projectId,
  propertyId,
  reviewStatus,
  workspaceId,
}: SetReviewStatusParams) =>
  put<SetReviewStatus['body'], SetReviewStatus['successResponse'], SetReviewStatus['path']>(
    `/api/workspaces/${toValue(workspaceId)}/projects/${toValue(projectId)}/entities/${toValue(entityId)}/properties/${toValue(propertyId)}/review_status`,
    {
      review_status: toValue(reviewStatus),
    },
  )
